import { useEffect, useState, useMemo, useRef } from "react";
import { useAppContext } from "../../../contexts/app";


const _timeToRefreshInMinuts = 15;

const convertTo12HourES6 = (time24) => {
	const [hourStr, minuteStr] = time24.split(':');
	let hour = Number(hourStr);
	const minute = Number(minuteStr);
	const ampm = hour >= 12 ? 'PM' : 'AM';
	hour = hour % 12 || 12;
	return `${hour}:${minute.toString().padStart(2, '0')} ${ampm}`;
}

const convertDurationToHours = (duration) => {
	// Converts "8:00" or "8.5" to a number representing hours
	if (typeof duration === 'number') return duration;
	if (typeof duration === 'string' && duration.includes(':')) {
		const [hourStr, minuteStr] = duration.split(':');
		const hours = Number(hourStr);
		const minutes = Number(minuteStr) || 0;
		return hours + (minutes / 60);
	}
	return Number(duration) || 8; // Default to 8 if parsing fails
}

export default function GoNoGo2() {
	const { loadSheetsBatchAsJson } = useAppContext();
	const date = new Date();
	const [goals, setGoals] = useState(null);
	const [todaysGoal, setTodaysGoal] = useState(null);
	const [today, setToday] = useState(`${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`);
	const [goal, setGoal] = useState(null);
	const [startTime, setStartTime] = useState(null);
	const [endTime, setEndTime] = useState(null);
	const [onTrack, setOnTrack] = useState(true);
	const [amountOfHours, setAmountOfHours] = useState(8);
	const [allCompletedUnits, setAllCompletedUnits] = useState([]); // Store the actual units data

	// Calculate completedToday from the data - no state update issues!
	const completedToday = useMemo(() => {
		if (!allCompletedUnits || allCompletedUnits.length === 0) {
			return 0;
		}

		const todayDate = new Date();
		const year = todayDate.getFullYear();
		const month = String(todayDate.getMonth() + 1).padStart(2, '0');
		const day = String(todayDate.getDate()).padStart(2, '0');
		const todayKey = `${year}-${month}-${day}`;

		const getDateKey = (dateString) => {
			const date = new Date(dateString);
			const y = date.getFullYear();
			const m = String(date.getMonth() + 1).padStart(2, '0');
			const d = String(date.getDate()).padStart(2, '0');
			return `${y}-${m}-${d}`;
		};

		const count = allCompletedUnits.filter(unit => {
			const timestamp = unit.final_signoff_user_Timestamp;
			if (!timestamp) return false;
			return getDateKey(timestamp) === todayKey;
		}).length;

		return count;
	}, [allCompletedUnits]);

	useEffect(() => {
		if (goals && goals.length > 0 && todaysGoal) {
			// Parse as numbers to ensure proper calculation
			setGoal(Number(todaysGoal.Goal) || 2);
			setStartTime(todaysGoal.Start || '06:00');
			setEndTime(todaysGoal.End || '14:00');
			setAmountOfHours(convertDurationToHours(todaysGoal.Hours));

			console.log('=== GOAL DATA LOADED ===');
			console.log('Raw goal:', todaysGoal.Goal, '| Parsed:', Number(todaysGoal.Goal));
			console.log('Raw hours:', todaysGoal.Hours, '| Parsed:', convertDurationToHours(todaysGoal.Hours));
			console.log('========================');
		}
	}, [todaysGoal]);

	// Calculate on-track status
	useEffect(() => {
		const calculateOnTrack = () => {
			if (!goal || !startTime || !endTime || !amountOfHours) {
				return;
			}

			const now = new Date();
			const currentHours = now.getHours();
			const currentMinutes = now.getMinutes();

			// Parse start and end times
			const [startHour, startMinute] = startTime.split(':').map(Number);
			const [endHour, endMinute] = endTime.split(':').map(Number);

			// Create Date objects for comparison
			const startDateTime = new Date();
			startDateTime.setHours(startHour, startMinute, 0, 0);

			const endDateTime = new Date();
			endDateTime.setHours(endHour, endMinute, 0, 0);

			// Before start time - show green
			if (now < startDateTime) {
				setOnTrack(true);
				return;
			}

			// Calculate units per hour
			const unitsPerHour = goal / amountOfHours;

			// After end time - compare against final goal
			if (now >= endDateTime) {
				setOnTrack(completedToday >= goal);
				return;
			}

			// During work hours - calculate expected units by current hour
			// Calculate how many complete hours have passed since start
			const minutesSinceStart = (currentHours * 60 + currentMinutes) - (startHour * 60 + startMinute);
			const hoursSinceStart = Math.floor(minutesSinceStart / 60);

			// Calculate expected units at this hour mark (rounded up)
			const expectedUnits = Math.ceil(unitsPerHour * hoursSinceStart);

			// On track if completed >= expected
			setOnTrack(completedToday >= expectedUnits);

			console.log('=== ON-TRACK CALCULATION ===');
			console.log('Current time:', `${currentHours}:${currentMinutes.toString().padStart(2, '0')}`);
			console.log('Start time:', startTime, '| End time:', endTime);
			console.log('Hours since start:', hoursSinceStart);
			console.log('Units per hour:', unitsPerHour);
			console.log('Expected units by hour', hoursSinceStart, ':', expectedUnits);
			console.log('Completed so far:', completedToday);
			console.log('On track?', completedToday >= expectedUnits);
			console.log('===========================');
		};

		calculateOnTrack();

		// Recalculate every 15 minutes (900000 ms)
		const interval = setInterval(calculateOnTrack, 900000);

		return () => clearInterval(interval);
	}, [goal, startTime, endTime, amountOfHours, completedToday]);

	// Load BOTH goals and completed units in ONE effect to avoid race conditions
	const isLoadingRef = useRef(false);

	useEffect(() => {
		let retryTimeout = null;

		const loadAllData = async () => {
			if (isLoadingRef.current) {
				return;
			}
			isLoadingRef.current = true;

			try {
				// Load both spreadsheets using the batch loader which handles switching correctly
				const results = await loadSheetsBatchAsJson(
					[process.env.REACT_APP_ONTRACK, process.env.REACT_APP_FS_Flock_V1],
					{ force: true, restore: true }
				);

				// Extract data from results
				const goalsResult = results.find(r => r.spreadsheetId === process.env.REACT_APP_ONTRACK);
				const unitsResult = results.find(r => r.spreadsheetId === process.env.REACT_APP_FS_Flock_V1);

				const goalsData = goalsResult?.data || [];
				const allUnits = unitsResult?.data || [];

				const todayGoal = goalsData.find(obj => obj.Date === today);

				// Debug: Check first unit's fields to verify we have the right spreadsheet
				if (allUnits && allUnits.length > 0) {
					const firstUnitFields = Object.keys(allUnits[0]);
					const hasUnitFields = firstUnitFields.includes('VIN') || firstUnitFields.includes('UUID');
					if (!hasUnitFields) {
						console.error('ERROR: Wrong spreadsheet loaded! Fields:', firstUnitFields.slice(0, 10));
						isLoadingRef.current = false;
						return; // Don't continue if wrong spreadsheet
					}
				}

				// Filter completed units - a unit is complete if final_signoff_user_Timestamp exists
				const completedUnits = allUnits.filter((unit) => {
					const timestamp = unit.final_signoff_user_Timestamp;
					const isValid = timestamp && timestamp !== '' && !isNaN(new Date(timestamp).getTime());
					return isValid;
				});

				// Update ALL state at once - this ensures React batches them together
				setGoals(goalsData);
				setTodaysGoal(todayGoal);
				setAllCompletedUnits(completedUnits);

				isLoadingRef.current = false;
			} catch (error) {
				isLoadingRef.current = false;

				// Check if it's a quota error (429)
				const isQuotaError = error?.message?.includes('429') ||
					error?.message?.includes('Quota exceeded') ||
					error?.response?.status === 429;

				if (isQuotaError) {
					console.warn('Google Sheets API quota exceeded. Retrying in 2 minutes...');
					// Retry after 2 minutes (120000 ms)
					retryTimeout = setTimeout(loadAllData, 120000);
				} else {
					console.error('Error loading data:', error);
				}
			}
		};

		if (today) {
			loadAllData();
		}

		const interval = setInterval(loadAllData, _timeToRefreshInMinuts * 1000 * 60); // Refresh every defined minutes

		return () => {
			clearInterval(interval);
			if (retryTimeout) clearTimeout(retryTimeout);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [today]); // Run when today changes


	return goal && startTime && endTime && <>
		<div className={`${onTrack ? 'tw-bg-green-500 tw-text-black' : 'tw-bg-red-600 tw-text-white'} tw-flex tw-flex-col tw-justify-center tw-leading-none tw-text-center tw-items-center tw-font-quaternary tw-font-quaternary-thin tw-tracking-wide tw-h-screen tw-w-full`}>
			<div className="tw-space-y-40d tw-pt-80d">
				<div className="tw-text-dynamic tw-font-quaternary-bold tw-leading-[.8] tw-flex tw-flex-col" style={{ '--max-font-size': '200', '--min-font-size': '1' }}>
					<span>
						<span>{completedToday}</span>
						<span className="tw-font-quaternary-thin">/</span>
						<span>{goal}</span>
					</span>
					<span  className="tw-font-quaternary-thin tw-text-[.2em] tw-tracking-widest tw-pt-20d">
						READY FOR INVOICING
					</span>
				</div>
				<div className="tw-space-y-5d">
					<div className="tw-text-dynamic tw-leading-none tw-leading-[.7] tw-opacity-50" style={{ '--max-font-size': '60', '--min-font-size': '1' }} dangerouslySetInnerHTML={{ __html: today.replaceAll('/', '<span class="tw-font-quaternary-normal">.</span>') }}></div>
					<div className="tw-text-dynamic tw-leading-none tw-leading-[.7] tw-opacity-50" style={{ '--max-font-size': '20', '--min-font-size': '1' }}>
						{startTime && endTime ? `${convertTo12HourES6(startTime)} - ${convertTo12HourES6(endTime)}` : ''}
					</div>
				</div>
			</div>
		</div>
	</>
}
